import { useState, useEffect } from "react";
import {
  getAllCalendarDates,
  updateCalendarDate,
  deleteCalendarDate,
} from "../..";

const CalendarDateTable = ({ refreshTrigger }) => {
  const [calendarDates, setCalendarDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchCalendarDates();
  }, [refreshTrigger]);

  const fetchCalendarDates = async () => {
    try {
      setLoading(true);
      const result = await getAllCalendarDates();
      if (result && Array.isArray(result)) {
        setCalendarDates(result);
      } else if (result?.error) {
        console.error("Error fetching calendar dates:", result.error);
        alert("Error fetching calendar dates: " + result.error);
      }
    } catch (error) {
      console.error("Error fetching calendar dates:", error);
      alert("Failed to fetch calendar dates");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (date) => {
    setEditingId(date._id);
    setEditForm({
      date: new Date(date.date).toISOString().split("T")[0],
      title: date.title,
      description: date.description || "",
      eventType: date.eventType,
      isActive: date.isActive,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleInputChange = (name) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setEditForm({ ...editForm, [name]: value });
  };

  const handleUpdate = async (id) => {
    try {
      const result = await updateCalendarDate(id, editForm);
      if (result.error) {
        alert(result.error);
      } else if (result.message) {
        alert(result.message);
        setEditingId(null);
        setEditForm({});
        fetchCalendarDates();
      }
    } catch (error) {
      alert("Failed to update calendar date");
      console.error(error);
    }
  };

  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        const result = await deleteCalendarDate(id);
        if (result.error) {
          alert(result.error);
        } else if (result.message) {
          alert(result.message);
          fetchCalendarDates();
        }
      } catch (error) {
        alert("Failed to delete calendar date");
        console.error(error);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getEventTypeColor = (eventType) => {
    const colors = {
      festival: "#dc3545",
      holiday: "#28a745",
      special: "#ffc107",
      event: "#007bff",
    };
    return colors[eventType] || "#6c757d";
  };

  const cardStyle = {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    border: "1px solid #e0e0e0",
    marginBottom: "20px",
  };

  const headerStyle = {
    backgroundColor: "#f8f9fa",
    color: "#2c3e50",
    borderBottom: "2px solid #e9ecef",
    padding: "16px 20px",
    borderRadius: "12px 12px 0 0",
    fontWeight: "600",
    fontSize: "18px",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  };

  const thStyle = {
    backgroundColor: "#f8f9fa",
    padding: "12px 16px",
    textAlign: "left",
    fontWeight: "600",
    color: "#495057",
    borderBottom: "2px solid #e9ecef",
  };

  const tdStyle = {
    padding: "12px 16px",
    borderBottom: "1px solid #e9ecef",
    verticalAlign: "top",
  };

  const buttonStyle = {
    padding: "6px 12px",
    margin: "2px",
    border: "none",
    borderRadius: "4px",
    fontSize: "12px",
    cursor: "pointer",
    transition: "background-color 0.3s ease",
  };

  const editButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#ffc107",
    color: "#212529",
  };

  const deleteButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#dc3545",
    color: "#ffffff",
  };

  const saveButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#28a745",
    color: "#ffffff",
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#6c757d",
    color: "#ffffff",
  };

  const inputStyle = {
    width: "100%",
    padding: "6px 8px",
    border: "1px solid #ced4da",
    borderRadius: "4px",
    fontSize: "12px",
  };

  const selectStyle = {
    ...inputStyle,
    cursor: "pointer",
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: "60px",
    resize: "vertical",
  };

  if (loading) {
    return (
      <div style={cardStyle}>
        <div style={headerStyle}>
          <i className="fas fa-calendar me-2"></i>
          Calendar Dates
        </div>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
          <p style={{ marginTop: "16px", color: "#6c757d" }}>
            Loading calendar dates...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <i className="fas fa-calendar me-2"></i>
        Calendar Dates ({calendarDates.length})
      </div>
      <div style={{ padding: "20px" }}>
        {calendarDates.length === 0 ? (
          <div
            style={{ textAlign: "center", padding: "40px", color: "#6c757d" }}
          >
            <i className="fas fa-calendar-times fa-3x mb-3"></i>
            <p>No calendar dates found. Add your first calendar date!</p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Title</th>
                  <th style={thStyle}>Description</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {calendarDates.map((date) => (
                  <tr key={date._id}>
                    <td style={tdStyle}>
                      {editingId === date._id ? (
                        <input
                          type="date"
                          value={editForm.date}
                          onChange={handleInputChange("date")}
                          style={inputStyle}
                        />
                      ) : (
                        <span style={{ fontWeight: "500", color: "#000000" }}>
                          {formatDate(date.date)}
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {editingId === date._id ? (
                        <input
                          type="text"
                          value={editForm.title}
                          onChange={handleInputChange("title")}
                          style={inputStyle}
                          maxLength="100"
                        />
                      ) : (
                        <span style={{ fontWeight: "600", color: "#000000" }}>
                          {date.title}
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {editingId === date._id ? (
                        <textarea
                          value={editForm.description}
                          onChange={handleInputChange("description")}
                          style={textareaStyle}
                          maxLength="500"
                        />
                      ) : (
                        <span style={{ color: "#6c757d", fontSize: "13px" }}>
                          {date.description || "No description"}
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {editingId === date._id ? (
                        <select
                          value={editForm.eventType}
                          onChange={handleInputChange("eventType")}
                          style={selectStyle}
                        >
                          <option value="event">Event</option>
                          <option value="festival">Festival</option>
                          <option value="holiday">Holiday</option>
                          <option value="special">Special</option>
                        </select>
                      ) : (
                        <span
                          style={{
                            backgroundColor: getEventTypeColor(date.eventType),
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            fontSize: "11px",
                            fontWeight: "500",
                            textTransform: "uppercase",
                          }}
                        >
                          {date.eventType}
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {editingId === date._id ? (
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={editForm.isActive}
                            onChange={handleInputChange("isActive")}
                          />
                          Active
                        </label>
                      ) : (
                        <span
                          style={{
                            color: date.isActive ? "#28a745" : "#dc3545",
                            fontWeight: "500",
                          }}
                        >
                          <i
                            className={`fas ${
                              date.isActive
                                ? "fa-check-circle"
                                : "fa-times-circle"
                            } me-1`}
                          ></i>
                          {date.isActive ? "Active" : "Inactive"}
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {editingId === date._id ? (
                        <div>
                          <button
                            style={saveButtonStyle}
                            onClick={() => handleUpdate(date._id)}
                            title="Save changes"
                          >
                            <i className="fas fa-save"></i>
                          </button>
                          <button
                            style={cancelButtonStyle}
                            onClick={handleCancelEdit}
                            title="Cancel edit"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ) : (
                        <div>
                          <button
                            style={editButtonStyle}
                            onClick={() => handleEdit(date)}
                            title="Edit calendar date"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            style={deleteButtonStyle}
                            onClick={() => handleDelete(date._id, date.title)}
                            title="Delete calendar date"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarDateTable;
